import { z } from 'zod';

// runtime config injected by preview-ui's entrypoint (/env.js); empty or
// absent everywhere else, so behaviour is unchanged [STMN-3418]
declare global {
  interface Window {
    __ENV__?: Record<string, unknown>;
  }
}

const configSchema = z.object({
  VITE_ACCOUNT_API_URL: z.string().url(),
  VITE_ANALYTICS_API_URL: z.string().url(),
  VITE_ANALYTICS_UI_URL: z.string().url(),
  VITE_TRACE_UI_URL: z.string(),
  VITE_ACCOUNT_UI_URL: z.string(),
  VITE_CLIENT_ID: z.string(),
  VITE_ENV: z.enum(['local', 'staging', 'preprod', 'prod', 'demo'])
});

type Config = z.infer<typeof configSchema>;

const configs = {
  local: {
    VITE_ACCOUNT_API_URL: 'https://account-api.stalocalging.stratumn.com:4200',
    VITE_ANALYTICS_API_URL: 'https://analytics-api.local.stratumn.com:4800',
    VITE_ANALYTICS_UI_URL: 'https://analytics.local.stratumn.com:4802',
    VITE_TRACE_UI_URL: 'https://trace.local.stratumn.com:4101',
    VITE_ACCOUNT_UI_URL: 'https://account.local.stratumn.com:4201',
    VITE_ENV: 'local',
    VITE_CLIENT_ID: '4'
  },
  staging: {
    VITE_ACCOUNT_API_URL: 'https://account-api.staging.stratumn.com',
    VITE_ANALYTICS_API_URL: 'https://analytics-api.staging.stratumn.com',
    VITE_ANALYTICS_UI_URL: 'https://analytics.staging.stratumn.com',
    VITE_TRACE_UI_URL: 'https://trace.staging.stratumn.com',
    VITE_ACCOUNT_UI_URL: 'https://account.staging.stratumn.com',
    VITE_ENV: 'staging',
    VITE_CLIENT_ID: '8',
  },
  preprod: {
    VITE_ACCOUNT_API_URL: 'https://account-api.preprod.stratumn.com',
    VITE_ANALYTICS_API_URL: 'https://analytics-api.preprod.stratumn.com',
    VITE_ANALYTICS_UI_URL: 'https://analytics.preprod.stratumn.com',
    VITE_TRACE_UI_URL: 'https://trace.preprod.stratumn.com',
    VITE_ACCOUNT_UI_URL: 'https://account.preprod.stratumn.com',
    VITE_ENV: 'preprod',
    VITE_CLIENT_ID: '4',
  },
  preprod_us: {
    VITE_ACCOUNT_API_URL: 'https://account-api.preprod.us.stratumn.com',
    VITE_ANALYTICS_API_URL: 'https://analytics-api.preprod.us.stratumn.com',
    VITE_ANALYTICS_UI_URL: 'https://analytics.preprod.us.stratumn.com',
    VITE_TRACE_UI_URL: 'https://trace.preprod.us.stratumn.com',
    VITE_ACCOUNT_UI_URL: 'https://account.preprod.us.stratumn.com',
    VITE_ENV: 'preprod',
    VITE_CLIENT_ID: '4',
  },
  release: {
    VITE_ACCOUNT_API_URL: 'https://account-api.stratumn.com',
    VITE_ANALYTICS_API_URL: 'https://analytics-api.stratumn.com',
    VITE_ANALYTICS_UI_URL: 'https://analytics.stratumn.com',
    VITE_TRACE_UI_URL: 'https://trace.stratumn.com',
    VITE_ACCOUNT_UI_URL: 'https://account.stratumn.com',
    VITE_ENV: 'prod',
    VITE_CLIENT_ID: '4',
  },
  release_us: {
    VITE_ACCOUNT_API_URL: 'https://account-api.us.stratumn.com',
    VITE_ANALYTICS_API_URL: 'https://analytics-api.us.stratumn.com',
    VITE_ANALYTICS_UI_URL: 'https://analytics.us.stratumn.com',
    VITE_TRACE_UI_URL: 'https://trace.us.stratumn.com',
    VITE_ACCOUNT_UI_URL: 'https://account.us.stratumn.com',
    VITE_ENV: 'prod',
    VITE_CLIENT_ID: '4',
  },
  demo: {
    VITE_ACCOUNT_API_URL: 'https://account-api.demo.stratumn.com',
    VITE_ANALYTICS_API_URL: 'https://analytics-api.demo.stratumn.com',
    VITE_ANALYTICS_UI_URL: 'https://analytics.demo.stratumn.com',
    VITE_TRACE_UI_URL: 'https://trace.demo.stratumn.com',
    VITE_ACCOUNT_UI_URL: 'https://account.demo.stratumn.com',
    VITE_ENV: 'demo',
    VITE_CLIENT_ID: '8'
  }
} satisfies Record<string, Config>;

type EnvName = keyof typeof configs;

const getConfig = () => {
  const hostname = window.location.hostname;
  let envConfig: Config;

  if (hostname.includes('staging')) {
    envConfig = configs.staging;
  } else if (hostname.includes('preprod.us')) {
    envConfig = configs.preprod_us;
  } else if (hostname.includes('preprod')) {
    envConfig = configs.preprod;
  } else if (hostname.includes('.us.')) {
    envConfig = configs.release_us;
  } else if (hostname.includes('local')) {
    envConfig = configs.local;
  } else if (hostname.includes('demo')) {
    envConfig = configs.demo;
  } else {
    envConfig = configs.release;
  }

  const mode = import.meta.env.VITE_MODE as EnvName | undefined;
  console.log('mode: ', mode);
  if (mode && mode in configs) {
    envConfig = configs[mode];
  }

  const mergedConfig = Object.keys(envConfig).reduce(
    (acc, key) => {
      const envValue = import.meta.env[key as keyof ImportMeta];
      acc[key] =
        envValue !== undefined ? envValue : envConfig[key as keyof Config];
      return acc;
    },
    {} as Record<string, any>
  );

  // Runtime override injected via /env.js (review apps): wins over build-time
  const runtimeConfig = {
    ...mergedConfig,
    ...(window.__ENV__ ?? {})
  };

  const validatedConfig = configSchema.safeParse(runtimeConfig);

  if (!validatedConfig.success) {
    console.error(validatedConfig.error);
    throw new Error('Configuration validation failed');
  }

  return validatedConfig.data;
};

export const env = getConfig();
